// T22.641: mounts the keep-wheels popup once for the whole app.
//
// * At login (first route with a token) it asks the server; an involuntary
//   downgrade that left the account over its limits opens the popup right away.
// * openKeepWheels(...) from anywhere (Archived gallery card, Settings >
//   Storage, right after a self-chosen downgrade) opens it on demand.
// * A blocked upload / create / clone (402 downgrade_over_limit) opens it too.
// * While the owner still has to choose, a banner stays at the top.
// * T22.747: the popup says WHICH action was blocked, and opening the Create
//   Wheel wizard while creating is blocked opens the popup instead of letting
//   the owner answer every step only to be refused at Build.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import request from "../../services/request";
import { getLoggedInUserToken } from "../../utils/helpers";
import KeepWheelsModal from "./KeepWheelsModal";
import { DOWNGRADE_OVER_LIMIT_CODE, getKeepWheels, saveKeepWheels } from "./api";
import { OPEN_KEEP_WHEELS_EVENT, openKeepWheels } from "./openKeepWheels";
import {
  BLOCKED_ACTION,
  blockedActionFromRequest,
  createIsBlocked,
} from "./blockedAction";
import { hideCreateWheelPopup } from "../../redux/reducers/createWheelPopup";

const unwrap = (res) => res?.data?.data || null;

const errorCode = (err) =>
  err?.response?.data?.code || err?.response?.data?.data?.code || null;

export const KeepWheelsBanner = ({ onOpen }) => (
  <div
    role="status"
    data-testid="keep-wheels-banner"
    style={{
      position: "sticky",
      top: 0,
      zIndex: 1200,
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "center",
      justifyContent: "center",
      padding: "10px 16px",
      background: "#FFF4E6",
      color: "#5C3D00",
      fontSize: 14,
      borderBottom: "1px solid #FFD8A8",
    }}
  >
    <span>
      Your plan changed and your wheels don't fit it yet. Uploads, new wheels and
      clones are paused until you choose which wheels to keep.
    </span>
    <button
      type="button"
      onClick={onOpen}
      style={{
        border: 0,
        borderRadius: 6,
        padding: "6px 12px",
        background: "#8F38EF",
        color: "#fff",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      Choose wheels
    </button>
  </div>
);

const KeepWheelsHost = () => {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [state, setState] = useState(null);
  const [params, setParams] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [blockedAction, setBlockedAction] = useState(null);
  const checkedToken = useRef(null);
  const dispatch = useDispatch();
  const createWizardOpen = useSelector((s) => !!s?.createWheelPopup?.isOpen);

  const load = useCallback(async (query = {}, { forceOpen = false } = {}) => {
    try {
      const data = unwrap(await getKeepWheels(query));
      if (!data?.enabled) return null;
      setState(data);
      setParams(query);
      const anyArchived = (data.wheels || []).some((w) => w.archived);
      if (data.must_choose || (forceOpen && (data.over_limit || anyArchived))) {
        setError("");
        setOpen(true);
      } else if (forceOpen) {
        toast.success("All your wheels fit your plan. Nothing to choose.");
      }
      return data;
    } catch (e) {
      if (forceOpen) toast.error("Couldn't load your wheels. Please try again.");
      return null;
    }
  }, []);

  // Login: the first route that has a token asks the server once per token.
  useEffect(() => {
    const token = getLoggedInUserToken();
    if (!token || checkedToken.current === token) return;
    checkedToken.current = token;
    load();
  }, [pathname, load]);

  // On demand from anywhere.
  useEffect(() => {
    const onOpen = (e) => {
      const d = e?.detail || {};
      const query = {};
      if (d.targetTier !== undefined && d.targetTier !== null) {
        query.target_tier = d.targetTier;
        if (d.intent) query.intent = d.intent;
      }
      setBlockedAction(d.blockedAction || null);
      load(query, { forceOpen: d.intent !== "self" });
    };
    window.addEventListener(OPEN_KEEP_WHEELS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_KEEP_WHEELS_EVENT, onOpen);
  }, [load]);

  // A refused upload / create / clone opens the popup.
  useEffect(() => {
    const id = request.interceptors.response.use(undefined, (err) => {
      if (errorCode(err) === DOWNGRADE_OVER_LIMIT_CODE) {
        // The popup itself names the blocked action (T22.747).
        openKeepWheels({ blockedAction: blockedActionFromRequest(err?.config) });
      }
      return Promise.reject(err);
    });
    return () => request.interceptors.response.eject(id);
  }, []);

  // T22.747: creating is blocked -- close the wizard before its first step and
  // explain in the popup, instead of a refusal at the last step.
  useEffect(() => {
    if (!createWizardOpen || !createIsBlocked(state)) return;
    dispatch(hideCreateWheelPopup());
    setBlockedAction(BLOCKED_ACTION.CREATE);
    setError("");
    setOpen(true);
  }, [createWizardOpen, state, dispatch]);

  const handleClose = () => {
    setOpen(false);
    setBlockedAction(null);
  };

  const handleSave = async (keepIds) => {
    setSaving(true);
    setError("");
    try {
      const body = { keep_ids: keepIds };
      if (params.target_tier !== undefined) body.target_tier = params.target_tier;
      const res = unwrap(await saveKeepWheels(body));
      setOpen(false);
      setBlockedAction(null);
      if (res?.deferred) {
        toast.success("Saved. Your choice applies when your current plan period ends.");
      } else {
        const n = res?.archived || 0;
        toast.success(
          n ? `Saved. ${n} wheel${n === 1 ? "" : "s"} Archived.` : "Saved."
        );
      }
      queryClient.invalidateQueries();
      load();
    } catch (e) {
      const msg = e?.response?.data?.message;
      setError(msg || "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {state?.must_choose && !open && (
        <KeepWheelsBanner onOpen={() => setOpen(true)} />
      )}
      {state && (
        <KeepWheelsModal
          open={open}
          state={state}
          saving={saving}
          error={error}
          blockedAction={blockedAction}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default KeepWheelsHost;
